import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Loader2, Plus } from "lucide-react";
import { listSurveys, queueAll } from "@/lib/fieldStore";
import { buildView } from "@/lib/fieldLocal";
import { useFieldSync } from "./FieldSync";

export const STATE_LABEL = {
  da_compilare: ["Da compilare", "bg-[#E3EEEE] text-[#0D4D4F]"],
  da_completare: ["Da completare", "bg-[#FBEFD2] text-[#8A5A00]"],
  in_coda: ["In coda: parte con la rete", "bg-[#FBEFD2] text-[#8A5A00]"],
  inviato: ["Inviato al consulente", "bg-[#DDF0E5] text-[#1F6B45]"],
  chiuso: ["Chiuso", "bg-[#EFE6D8] text-[#5C6667]"],
};

export default function FieldHome() {
  const { pack, tick, online } = useFieldSync();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const [recs, queue] = await Promise.all([listSurveys(), queueAll()]);
      if (!alive) return;
      setRows(recs.sort((a, b) => String(b.local_at || "").localeCompare(String(a.local_at || ""))).map((r) => ({
        rec: r, pending: queue.filter((o) => o.survey_id === r.id).length, view: pack ? buildView(pack, r) : null })));
    })();
    return () => { alive = false; };
  }, [pack, tick]);
  if (!rows) return <div className="grid place-items-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  return (
    <div className="space-y-3">
      <h1 className="font-heading text-2xl font-bold">I tuoi sopralluoghi</h1>
      {!pack && <p className="rounded-2xl bg-[#FBEFD2] p-4 text-[#8A5A00]">{online ? "Preparo l'app per lavorare anche senza rete…" : "Per lavorare senza rete devi aprire l'app almeno una volta con internet."}</p>}
      <button onClick={() => navigate("/campo/nuovo")} disabled={!pack} data-testid="field-new-button"
        className="flex min-h-[72px] w-full items-center justify-center gap-3 rounded-2xl border-2 border-[#0D4D4F] bg-[#0D4D4F] text-xl font-bold text-white disabled:opacity-40">
        <Plus className="h-6 w-6" /> Nuovo sopralluogo
      </button>
      {rows.length === 0 && <p className="rounded-xl bg-white p-5 text-[#5C6667]">Nessun sopralluogo ancora. Quando arrivi in un'azienda premi «Nuovo sopralluogo»: apri tutto da qui, senza passare dal consulente. Funziona anche senza rete: appena torna, i dati partono da soli.</p>}
      {rows.map(({ rec, pending, view }) => {
        const [label, cls] = view ? (STATE_LABEL[view.state] || [view.state, ""]) : ["…", ""];
        return (
          <button key={rec.id} onClick={() => navigate(`/campo/${rec.id}`)} data-testid="field-survey-card"
            className="flex min-h-[88px] w-full items-center gap-4 rounded-2xl border-2 border-[#D9CBB4] bg-white p-4 text-left shadow-sm hover:border-[#0D4D4F]">
            <ClipboardList className="h-9 w-9 shrink-0 text-[#0D4D4F]" />
            <span className="min-w-0 flex-1">
              <b className="block truncate text-lg">{rec.title || (rec.company || {}).business_name || "Sopralluogo"}</b>
              <span className="block truncate text-sm text-[#5C6667]">{(rec.company || {}).business_name}{pending ? ` · ${pending} da inviare` : ""}</span>
              {view && view.change_request && <span className="mt-1 block text-sm text-[#8B1E2D]">Il consulente chiede: {view.change_request}</span>}
              {rec.problems && rec.problems.length > 0 && <span className="mt-1 block text-sm text-[#8B1E2D]">Attenzione: {rec.problems.length} avviso/i da leggere</span>}
            </span>
            {view && <span className="text-right">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${cls}`}>{label}</span>
              <span className="mt-1 block text-lg font-extrabold text-[#0D4D4F]">{view.progress.percent}%</span>
            </span>}
          </button>
        );
      })}
    </div>
  );
}
